import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import Modal from '../components/common/Modal';
import { Plus, Edit, Trash2, Users, Search, Phone, Mail, CreditCard, IdCard, List, UserPlus } from 'lucide-react';
import { associateService } from '../services/associateService';
import { Associate } from '../types';
import { formatCPF, formatPhone, formatDate } from '../utils/formatters';
import { getImageUrl } from '../utils/imageHelper';

export const Associates: React.FC = () => {
  const navigate = useNavigate();
  const [associates, setAssociates] = useState<Associate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [deletingAssociate, setDeletingAssociate] = useState<Associate | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchAssociates();
  }, [currentPage, search]);

  const fetchAssociates = async () => {
    try {
      setLoading(true);
      const response = await associateService.getAll(currentPage, search);
      setAssociates(response.data);
      setTotalPages(response.last_page);
    } catch (err: any) {
      setError('Erro ao carregar associados');
      console.error('Associates fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingAssociate) return;

    try {
      await associateService.delete(Number(deletingAssociate.id));
      setSuccess('Associado excluído com sucesso!');
      setShowDeleteModal(false);
      setDeletingAssociate(null);
      fetchAssociates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir associado');
      setShowDeleteModal(false);
      setDeletingAssociate(null);
    }
  };

  const handleUpdateStatus = async (associate: Associate, status: 'ativo' | 'inativo' | 'suspenso') => {
    try {
      await associateService.updateStatus(Number(associate.id), status);
      setSuccess(`Status do associado atualizado para ${status}!`);
      fetchAssociates();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar status');
    }
  };

  const openDeleteModal = (associate: Associate) => {
    setDeletingAssociate(associate);
    setShowDeleteModal(true);
  };

  const getStatusVariant = (status?: string) => {
    switch (status) {
      case 'ativo':
        return 'success';
      case 'suspenso':
        return 'warning';
      case 'inativo':
        return 'danger';
      default:
        return 'info';
    }
  };

  if (loading && associates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Associados</h1>
            <p className="text-gray-600 mt-2">
              Gerencie os associados que utilizam os benefícios do cartão
            </p>
          </div>
          <Button
            onClick={() => navigate('/associados/novo')}
            className="flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Novo Associado
          </Button>
        </div>

        {/* Alerts */}
        {error && (
          <Alert type="error" className="mb-6" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert type="success" className="mb-6" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Search */}
        <div className="mb-6">
          <Input
            type="text"
            placeholder="Buscar por nome, CPF ou email..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            icon={<Search className="h-5 w-5" />}
          />
        </div>

        {/* Content */}
        {associates.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhum associado cadastrado
              </h3>
              <p className="text-gray-600 mb-6">
                Comece adicionando o primeiro associado ao sistema
              </p>
              <Button
                onClick={() => navigate('/associados/novo')}
                className="flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Adicionar Associado
              </Button>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {associates.map((associate) => (
                <Card key={associate.id} className="hover:shadow-lg transition-shadow">
                  <div className="p-6">
                    {/* Header com Foto */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1">
                        {associate.foto && getImageUrl(associate.foto) ? (
                          <img
                            src={getImageUrl(associate.foto)!}
                            alt={associate.nome || associate.name}
                            className="w-12 h-12 rounded-full object-cover"
                            onError={(e) => {
                              // Mostrar ícone padrão se falhar ao carregar
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                const fallback = document.createElement('div');
                                fallback.className = 'w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center';
                                fallback.innerHTML = '<svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>';
                                parent.insertBefore(fallback, e.currentTarget);
                              }
                            }}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="w-6 h-6 text-blue-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">
                            {associate.nome || associate.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {formatCPF(associate.cpf)}
                          </p>
                        </div>
                      </div>
                      <Badge variant={getStatusVariant(associate.status)}>
                        {associate.status || (associate.active ? 'Ativo' : 'Inativo')}
                      </Badge>
                    </div>

                    {/* Informações */}
                    <div className="space-y-2 mb-4 text-sm text-gray-600">
                      {associate.email && (
                        <div className="flex items-start gap-2">
                          <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-1">{associate.email}</span>
                        </div>
                      )}

                      <div className="flex items-start gap-2">
                        <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{formatPhone(associate.telefone || associate.phone)}</span>
                      </div>

                      <div className="flex items-start gap-2">
                        <IdCard className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>Matrícula: {associate.registration_number}</span>
                      </div>

                      {associate.card_number && (
                        <div className="flex items-start gap-2">
                          <CreditCard className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-600" />
                          <span className="font-medium text-blue-600">
                            Cartão: {associate.card_number}
                          </span>
                        </div>
                      )}

                      {associate.data_nascimento && (
                        <div className="flex items-start gap-2 text-xs text-gray-500 mt-2">
                          Nascimento: {formatDate(associate.data_nascimento)}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-4 border-t border-gray-200">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(`/associados/${associate.id}/editar`)}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/associados/${associate.id}/planos`)}
                        title="Gerenciar Planos"
                      >
                        <List className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/associados/${associate.id}/dependentes`)}
                        title="Gerenciar Dependentes"
                      >
                        <UserPlus className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => openDeleteModal(associate)}
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deletingAssociate && (
          <Modal
            isOpen={true}
            onClose={() => {
              setShowDeleteModal(false);
              setDeletingAssociate(null);
            }}
            title="Confirmar Exclusão"
          >
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Tem certeza que deseja excluir o associado{' '}
                <strong>{deletingAssociate.nome || deletingAssociate.name}</strong>?
                Esta ação não pode ser desfeita.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingAssociate(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button variant="danger" onClick={handleDelete}>
                  Excluir
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};
